import { HelpCircle } from 'react-feather';
import { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetEventRegistrationsStatsQuery as GetEventRegistrationsStats } from '__generated__/graphql';

import { FilterIndicator, StatsListHeader, StickyStatsListCard, StickyStatsListHeader } from './Layout';
import TicketPromotionStats from './TicketPromotionStats';
import TicketStats, { RevenueColumnOption, SalesColumnOption } from './TicketStats';
import TimeSlotStats from './TimeSlotStats';
import UI from '../../common/UI';
import useMediaDevice from '../../common/useMediaDevice';
import useOnClickOutside from '../../common/useOnClickOutside';
import useScroll from '../../common/useScroll';

type Event = GetEventRegistrationsStats['event'];
type Ticket = Event['tickets'][0];

export interface TicketStatsProps {
  event: Event;
  tickets: Ticket[];
}

const TicketStatsContainer = memo(({ event, tickets } : TicketStatsProps) => {
  const { t } = useTranslation();
  const { scrollToIfInvisible } = useScroll();
  const device = useMediaDevice();

  const [activeTab, setActiveTab] = useState<'tickets' | 'time_slots' | 'promotions'>('tickets');

  const toggleTab = (tab: typeof activeTab) => {
    setActiveTab(tab);

    const timer = window.setTimeout(() => {
      scrollToIfInvisible('TicketStatsContainer');
    });

    return () => window.clearTimeout(timer);
  };
  const hasTimeSlots = tickets.filter((ticket) => ticket.time_slots.length > 0).length > 0;

  const showTimeSlots = activeTab === 'time_slots' && hasTimeSlots;
  const showPromotions = activeTab === 'promotions';
  const showTickets = activeTab === 'tickets' || (!showTimeSlots && !showPromotions);

  const [salesColumn, setSalesColumn] = useState<SalesColumnOption>('sales');
  const [showingSalesColumnOptions, setShowingSalesColumnOptions] = useState(false);
  const salesColumnOptionsDropdownRef = useRef();
  useOnClickOutside(salesColumnOptionsDropdownRef, () => setShowingSalesColumnOptions(false));

  const [revenueColumn, setRevenueColumn] = useState<RevenueColumnOption>('revenue');
  const [showingRevenueColumnOptions, setShowingRevenueColumnOptions] = useState(false);
  const revenueColumnOptionsDropdownRef = useRef();
  useOnClickOutside(revenueColumnOptionsDropdownRef, () => setShowingRevenueColumnOptions(false));

  const hideOptionsAnd = (...callbacks: (() => void)[]) => {
    setShowingSalesColumnOptions(false);
    setShowingRevenueColumnOptions(false);
    callbacks.map((callback) => callback());
  };

  return (
    <StickyStatsListCard id="TicketStatsContainer">
      {tickets.length > 0 && (
        <StickyStatsListHeader>
          <StatsListHeader>
            <UI.FlexContainer sc={{ alignItems: 'center' }}>
              <UI.H4>
                <UI.A
                  role="button"
                  onClick={() => toggleTab('tickets')}
                  sc={{
                    color: showTickets ? 'gray.800' : undefined,
                    mr: 2,
                    fontWeight: showTickets ? 600 : 400,
                  }}
                >
                  {t('tickets')}
                </UI.A>
              </UI.H4>
              <UI.H4>
                <UI.A
                  role="button"
                  onClick={() => toggleTab('promotions')}
                  sc={{
                    color: showPromotions ? 'gray.800' : undefined,
                    mr: 2,
                    fontWeight: showPromotions ? 600 : 400,
                  }}
                >
                  {t('promotions')}
                </UI.A>
              </UI.H4>
              {hasTimeSlots && (
                <UI.H4>
                  <UI.A
                    role="button"
                    onClick={() => toggleTab('time_slots')}
                    sc={{
                      color: showTimeSlots ? 'gray.800' : undefined,
                      mr: 2,
                      fontWeight: showTimeSlots ? 600 : 400,
                    }}
                  >
                    {t('time_slots')}
                  </UI.A>
                </UI.H4>
              )}
              <FilterIndicator />
            </UI.FlexContainer>
            {!device.isSmall && <UI.Div />}
            <UI.DropdownOptionsContainer ref={salesColumnOptionsDropdownRef}>
              <UI.Legend sc={{ small: true }}>
                <UI.A
                  role="button"
                  onClick={() => setShowingSalesColumnOptions((showing) => !showing)}
                  sc={{ noWrap: true }}
                >
                  {t(salesColumn === 'sales' ? 'registrations' : salesColumn)}
                  <UI.Caret />
                </UI.A>
              </UI.Legend>
              <UI.Dropdown
                sc={{ open: showingSalesColumnOptions, alignH: 'right', alignV: 'bottom' }}
              >
                <UI.Button
                  onClick={() => hideOptionsAnd(() => setSalesColumn('sales'))}
                  sc={{ textAlign: 'left', strong: salesColumn === 'sales' }}
                >
                  {t('registrations')}
                </UI.Button>
                <UI.Button
                  onClick={() => hideOptionsAnd(() => setSalesColumn('reservations'))}
                  sc={{ textAlign: 'left', strong: salesColumn === 'reservations' }}
                >
                  {t('reservations')}
                </UI.Button>
                <UI.Button
                  onClick={() => hideOptionsAnd(() => setSalesColumn('available'))}
                  sc={{ textAlign: 'left', strong: salesColumn === 'available' }}
                >
                  {t('available')}
                </UI.Button>
              </UI.Dropdown>
            </UI.DropdownOptionsContainer>
            {!device.isSmall && (
              <UI.DropdownOptionsContainer ref={revenueColumnOptionsDropdownRef}>
                <UI.Legend sc={{ small: true }}>
                  <UI.A
                    role="button"
                    onClick={() => setShowingRevenueColumnOptions((showing) => !showing)}
                  >
                    {t(revenueColumn)}
                    {revenueColumn === 'average_selling_price' && (
                      <>
                        {' '}
                        <UI.TooltipContainer sc={{ inline: true }}>
                          <UI.Icon sc={{ muted: true, mb: 0.25, mt: -0.25 }}>
                            <HelpCircle />
                          </UI.Icon>
                          <UI.Tooltip sc={{ alignH: 'right' }}>
                            {t('excludes_free_tickets')}
                          </UI.Tooltip>
                        </UI.TooltipContainer>
                      </>
                    )}
                    <UI.Caret />
                  </UI.A>
                </UI.Legend>
                <UI.Dropdown
                  sc={{ open: showingRevenueColumnOptions, alignH: 'right', alignV: 'bottom' }}
                >
                  <UI.Button
                    onClick={() => hideOptionsAnd(() => setRevenueColumn('revenue'))}
                    sc={{ textAlign: 'left', strong: revenueColumn === 'revenue' }}
                  >
                    {t('revenue')}
                  </UI.Button>
                  <UI.Button
                    onClick={() => hideOptionsAnd(() => setRevenueColumn('remaining'))}
                    sc={{ textAlign: 'left', strong: revenueColumn === 'remaining' }}
                  >
                    {t('remaining')}
                  </UI.Button>
                  <UI.Button
                    onClick={() => hideOptionsAnd(() => setRevenueColumn('average_selling_price'))}
                    sc={{ textAlign: 'left', strong: revenueColumn === 'average_selling_price' }}
                  >
                    {t('average_selling_price')}
                  </UI.Button>
                </UI.Dropdown>
              </UI.DropdownOptionsContainer>
            )}
          </StatsListHeader>
        </StickyStatsListHeader>
      )}
      {tickets.length === 0 && (
        <UI.Div sc={{ pt: 4 * (2 / 3), pb: 4 / 3 }}>
          <StatsListHeader>
            <UI.H4>
              {t('tickets')}
            </UI.H4>
          </StatsListHeader>
        </UI.Div>
      )}
      {showTickets && (
        <TicketStats
          event={event}
          tickets={tickets}
          salesColumn={salesColumn}
          revenueColumn={revenueColumn}
        />
      )}
      {showTimeSlots && (
        <TimeSlotStats
          tickets={tickets}
          salesColumn={salesColumn}
          revenueColumn={revenueColumn}
        />
      )}
      {showPromotions && (
        <TicketPromotionStats
          tickets={tickets}
          salesColumn={salesColumn}
          revenueColumn={revenueColumn}
        />
      )}
    </StickyStatsListCard>
  );
});

export default TicketStatsContainer;
